import React, {useEffect, useRef, useState, useCallback} from 'react'
import Drawer from 'react-modern-drawer'
import {createGitHubIntegration} from 'app/Integrations/Github'
import {useAuth} from 'app/modules/auth'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  onSnapshot,
} from 'firebase/firestore'
import db from 'services/firebase'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {showErrorToast, showInfoToast, showSuccessToast} from 'app/components/ShowToast'
import DrawerHeader from '../drawer-components/DrawerHeader'
import {DrawerContent} from '../drawer-components/DrawerContent'
import Overlay from 'app/components/Overlay'
import DrawerFooter from '../drawer-components/DrawerFooter'
import InfoCard from '../drawer-components/InfoCard'
import {AddDrawerIntegrationProps, Repository} from '../utils/types'
import { removeIntegrationTests } from 'app/Integrations/services/integrationTestsService'
import { evaluateGitHubTests } from 'app/Integrations/services/automatedTestEvaluator'

const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || ''

const GithubDrawer: React.FC<AddDrawerIntegrationProps> = ({id, isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(false)
  const [openAccordion2, setOpenAccordion2] = useState(false)
  const {currentUser} = useAuth()
  const [showAlert, setShowAlert] = useState(false)
  const drawerTopRef = useRef<HTMLDivElement>(null)
  const [integration, setIntegration] = useState<any[]>([])
  const [selectedOrg, setSelectedOrg] = useState('')
  const [isOrgLocked, setIsOrgLocked] = useState(false)
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem('github_access_token')
  )
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const navigate = useNavigate()
  const location = useLocation()
  const {code} = useParams()
  const queryParams = new URLSearchParams(location.search)
  const integrationIdFromUrl = queryParams.get('id') || id
  const [orgLoading, setOrgLoading] = useState(false)
  const isConnected = integration.length > 0 && integration[0]?.status === 'Connected'

  useEffect(() => {
    if (code) {
      localStorage.setItem('github_access_token', code)
      navigate('/integrations', {replace: true})
    }
  }, [code, navigate])

  const getIntegration = useCallback(() => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!integrationIdFromUrl) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', integrationIdFromUrl)
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data()
          if (data.name === 'GitHub') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              accessToken: data.accessToken,
              organizations: data.organizations || [],
            }
            setIntegration([integrationData])
            setAccessToken(data.accessToken)
            if (data.organizations?.length > 0 && data.orgId) {
              setSelectedOrg(data.orgId)
              setIsOrgLocked(true)
            }
          }
        }
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting integration:', error)
    }
  }, [currentUser?.tenantId, integrationIdFromUrl])

  useEffect(() => {
    if (isOpen && integrationIdFromUrl) {
      getIntegration()
    }
  }, [integrationIdFromUrl, isOpen, getIntegration])

  useEffect(() => {
    if (integration.length > 0 && integration[0]?.organizations?.length > 0) {
      if (!selectedOrg) {
        setFormErrors((prev) => ({...prev, selectedOrg: 'Please select an organization'}))
      } else {
        setFormErrors((prev) => ({...prev, selectedOrg: ''}))
      }
    }
  }, [integration, selectedOrg])

  useEffect(() => {
    if (showAlert && drawerTopRef.current) {
      drawerTopRef.current.scrollIntoView({behavior: 'smooth'})
    }
  }, [showAlert])

  const handleConnect = async () => {
    if (!accessToken) {
      setIsLoading(true)
      localStorage.setItem('pendingIntegration', 'GitHub')
      const authUrl = `https://github.com/login/oauth/authorize?client_id=${GITHUB_CLIENT_ID}&scope=repo%20repo:security_events%20admin:org%20read:org%20user`
      window.location.href = authUrl
      return
    }
  }

  const handleClose = () => {
    setShowAlert(false)
    onClose()
  }

  const handleSubmit = async (repos: Repository[]): Promise<number> => {
    let addedReposCount = 0

    try {
      for (const repo of repos) {
        const newAsset = {
          is_repo: true,
          repo_name: repo.name || 'Unnamed Repository',
          repo_link: repo.html_url || '',
          platform: 'GitHub',
          visibility: repo.private ? 'Private' : 'Public',
          description: repo.description || '',
          created_at: repo.created_at || new Date().toISOString(),
          updated_at: repo.updated_at || new Date().toISOString(),
          pushed_at: repo.pushed_at || new Date().toISOString(),
          size: repo.size || 0,
          stars: repo.stargazers_count || 0,
          watchers: repo.watchers_count || 0,
          language: repo.language || '',
          forks: repo.forks_count || 0,
          open_issues: repo.open_issues_count || 0,
          default_branch: repo.default_branch || 'main',
          owner: {
            login: repo.owner?.login || '',
            id: repo.owner?.id || 0,
            url: repo.owner?.html_url || '',
            type: repo.owner?.type || 'Organization',
          },
          tenantId: currentUser?.tenantId || '',
          uid: currentUser?.uid || '',
          createdAt: serverTimestamp(),
          audit_scope: true,
        }

        if (currentUser && currentUser.tenantId) {
          const tenantRef = doc(db, 'tenants', currentUser.tenantId)
          const policyRef = collection(tenantRef, 'assets')

          const querySnapshot = await getDocs(
            query(policyRef, where('repo_link', '==', repo.html_url))
          )

          if (querySnapshot.empty) {
            await addDoc(policyRef, newAsset)
            addedReposCount++

            const currentDate = new Date()
            const todaysDate = `${
              currentDate.getMonth() + 1
            }/${currentDate.getDate()}/${currentDate.getFullYear()}`

            const activitiesRef = collection(tenantRef, 'activities')
            await addDoc(activitiesRef, {
              message: `${currentUser?.userName} added a new asset '${repo.name}'`,
              timestamp: serverTimestamp(),
              tenantId: currentUser?.tenantId,
              uid: currentUser?.uid,
              action: 'Asset added',
              target: 'Asset Management',
              email: currentUser?.email,
              username: currentUser?.userName,
              date: todaysDate,
            })
          }
        }
      }

      if (addedReposCount > 0) {
        showSuccessToast({message: 'New repositories added successfully'})
      } else {
        showInfoToast({message: 'No new repositories were added'})
      }

      return addedReposCount
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showErrorToast({message: `Failed to add repositories: ${errorMessage}`})
      return 0
    } finally {
    }
  }

  const saveOrgMembers = async (members: any[], orgLogin: string, tenantId: string) => {
    try {
      const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'github')
      const membersMap: {[key: string]: any} = {}

      for (const member of members) {
        if (member.login) {
          membersMap[member.login] = {
            platform: 'GitHub',
            login: member.login,
            name: member.name || member.login,
            isTwoFactorEnabled: member.has2FA || false,
            accountRole: member.role || '',
            orgLogin,
            tenantId,
            uid: currentUser?.uid || '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            status: 'active',
            org: {
              login: orgLogin,
              name:
                integration[0]?.organizations.find((o: any) => o.login === orgLogin)?.name ||
                orgLogin,
            },
          }
        }
      }

      const githubDoc = await getDoc(accessReviewRef)

      if (githubDoc.exists()) {
        await updateDoc(accessReviewRef, {
          members: {
            ...githubDoc.data().members,
            ...membersMap,
          },
          updatedAt: serverTimestamp(),
        })
      } else {
        await setDoc(accessReviewRef, {
          platform: 'GitHub',
          members: membersMap,
          tenantId,
          uid: currentUser?.uid || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          orgLogin,
        })
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error saving organization members:', errorMessage)
      showErrorToast({message: 'Failed to save organization members'})
      throw error
    }
  }

  const handleOrgSelect = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const orgLogin = e.target.value
    setSelectedOrg(orgLogin)

    if (!orgLogin) {
      setFormErrors((prev) => ({...prev, selectedOrg: 'Please select an organization'}))
      return
    }

    setFormErrors((prev) => ({...prev, selectedOrg: ''}))

    try {
      setOrgLoading(true)
      const tenantId = currentUser?.tenantId || ''
      if (!integrationIdFromUrl || !tenantId) return

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', integrationIdFromUrl)
      const selectedOrgData = integration[0].organizations.find((o: any) => o.login === orgLogin)

      await updateDoc(integrationRef, {
        orgId: orgLogin,
        selectedOrg: selectedOrgData,
      })

      const githubIntegration = createGitHubIntegration(accessToken || '')
      const [reposResponse, membersResponse] = await Promise.all([
        githubIntegration.getOrgRepositories(orgLogin),
        githubIntegration.checkUser2FA(orgLogin),
      ])

      if (reposResponse.success && reposResponse.repositories) {
        await handleSubmit(reposResponse.repositories)
      }
      if (membersResponse.success && membersResponse.users) {
        await saveOrgMembers(membersResponse.users, orgLogin, tenantId)
      }

      try {
        const integrationData = {
          repositories: reposResponse.repositories || [],
          members: membersResponse.users || [],
          organizations: integration[0]?.organizations || []
        };
        
        await evaluateGitHubTests(tenantId, integrationData);
      } catch (testError) {
        console.error('Error evaluating GitHub tests:', testError);
      }

      setIsOrgLocked(true)
      showSuccessToast({message: 'Organization selected and data loaded successfully'})
    } catch (error) {
      console.error('Error handling organization selection:', error)
      showErrorToast({message: 'Failed to process organization selection'})
    } finally {
      setOrgLoading(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      setIsLoading(true)
      const tenantId = currentUser?.tenantId || ''
      if (!integrationIdFromUrl || !tenantId) return
      const tenantRef = doc(db, 'tenants', tenantId)

      const assetsRef = collection(tenantRef, 'assets')
      const githubAssetsQuery = query(assetsRef, where('platform', '==', 'GitHub'))
      const githubAssetsSnapshot = await getDocs(githubAssetsQuery)

      const deleteAssetPromises = githubAssetsSnapshot.docs.map(async (assetDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'assets', assetDoc.id))
      })

      await Promise.all(deleteAssetPromises)

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', integrationIdFromUrl)
      await deleteDoc(integrationRef)

      const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'github')
      await deleteDoc(accessReviewRef)

      const vendorsRef = collection(tenantRef, 'vendors')
      const githubVendorQuery = query(vendorsRef, where('vendor_name', '==', 'GitHub'))
      const githubVendorSnapshot = await getDocs(githubVendorQuery)

      const deleteVendorPromises = githubVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id))
      })

      await Promise.all(deleteVendorPromises)

      try {
        await removeIntegrationTests('GitHub', tenantId, currentUser);
      } catch (testError) {
        console.error('Error removing GitHub integration tests:', testError);
      }

      localStorage.removeItem('github_access_token')

      const activitiesRef = collection(tenantRef, 'activities')
      const currentDate = new Date()
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected GitHub integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })

      showSuccessToast({message: 'GitHub integration disconnected'})

      setIntegration([])
      setSelectedOrg('')
      setIsOrgLocked(false)
      handleClose()
    } catch (error) {
      showErrorToast({message: 'Failed to disconnect GitHub'})
    } finally {
      setIsLoading(false)
    }
  }

  const OrganizationSelector = () => {
    if (!integration[0]?.organizations?.length) return null

    return (
      <div className='mb-4'>
        <label className='fw-semibold mb-2 fs-6'>Select Organization</label>
        <select
          className={`form-select ${formErrors.selectedOrg ? 'is-invalid' : ''}`}
          value={selectedOrg}
          onChange={handleOrgSelect}
          disabled={isOrgLocked}
        >
          <option value=''>Select Organization</option>
          {integration[0].organizations.map((org: any) => (
            <option key={org.id} value={org.login}>
              {org.login}
            </option>
          ))}
        </select>
        {formErrors.selectedOrg && <span className='text-danger'>{formErrors.selectedOrg}</span>}
        {isOrgLocked && (
          <small className='text-muted'>
            Organization selection is locked. To change, disconnect the integration.
          </small>
        )}
      </div>
    )
  }

  return (
    <Drawer open={isOpen} onClose={handleClose} direction='right' className='' size='45%'>
      <DrawerHeader title='GitHub' onClose={onClose} />
      <div
        className='scroll-y overflow-auto p-6 mb-10'
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
        <div className='form'>
          <DrawerContent
            title='GitHub'
            description='Code Collaboration & Version Control Platform'
            logo='/media/integrations/github.svg'
            integration={integration}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            openAccordion2={openAccordion2}
            setOpenAccordion2={setOpenAccordion2}
            permissionInfo='SecureSlate will be granted permissions to read GitHub repositories, issues, and pull requests via OAuth 2.0, respecting user permissions.'
            accessTypeinfo='Requires an active GitHub integration with sufficient permissions to access repository and organization data.'
          />
          <div className='mb-8 d-flex justify-content-between align-items-center'>
            <div className='fs-6 fw-bold text-bold'>
              {isConnected ? 'GitHub Connected to SecureSlate' : 'Connect GitHub to SecureSlate'}{' '}
              <Overlay content='Upon successful connection, select an organization to import its repositories and members into the Asset Repository and Access Review sections of the platform.' />
              {orgLoading && (
                <span
                  className='badge px-3 rounded-pill ms-4'
                  style={{border: '1px solid #E1E1E1'}}
                >
                  <span className='fs-7'>Updating...</span>
                </span>
              )}
            </div>
          </div>
          <OrganizationSelector />
          {!isConnected && (
            <InfoCard
              type='info'
              title='Connect GitHub to SecureSlate'
              description='Click on the Connect button below. You will be redirected to GitHub to authenticate via OAuth and authorize SecureSlate. Once authorized, select an organization to import its repositories and members.'
            />
          )}
        </div>
      </div>
      <DrawerFooter
        isConnected={isConnected}
        isLoading={isLoading}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        customActions={!isConnected}
      />
    </Drawer>
  )
}

export default GithubDrawer
